import { useState, useEffect } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { apiFetch } from '../lib/api'
import { PineScriptEditor, defaultPineScript } from '../components/PineScriptEditor'

interface Strategy {
  id: string
  agent_id: string
  name: string
  description: string
  code: string
  strategy_type: string
  is_public: boolean
}

const strategyTypes = [
  { value: 'trend_following', label: '📈 Trend Following', desc: 'Follow established market trends' },
  { value: 'mean_reversion', label: '🔄 Mean Reversion', desc: 'Trade reversions to average price' },
  { value: 'momentum', label: '🚀 Momentum', desc: 'Capitalize on price momentum' },
  { value: 'scalping', label: '⚡ Scalping', desc: 'Quick trades for small profits' },
  { value: 'ml_based', label: '🧠 Machine Learning', desc: 'AI/ML driven strategies' },
  { value: 'arbitrage', label: '⚖️ Arbitrage', desc: 'Exploit price differences' },
  { value: 'grid_trading', label: '📊 Grid Trading', desc: 'Grid-based order placement' },
  { value: 'other', label: '🔧 Other', desc: 'Custom strategy type' },
]

export default function StrategyEditor() {
  const navigate = useNavigate()
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const forkFrom = searchParams.get('fork')
  
  const isEditing = Boolean(id)
  
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [code, setCode] = useState(defaultPineScript)
  const [strategyType, setStrategyType] = useState('trend_following')
  const [isPublic, setIsPublic] = useState(true)
  const [apiKey, setApiKey] = useState('')
  const [showApiKeyInput, setShowApiKeyInput] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  // Load existing strategy if editing
  const { data: existingStrategy } = useQuery<{ data: Strategy }>({
    queryKey: ['strategy', id],
    queryFn: () => apiFetch(`/api/strategies/${id}`),
    enabled: Boolean(id),
  })

  // Load strategy to fork
  const { data: forkStrategy } = useQuery<{ data: Strategy }>({
    queryKey: ['strategy-fork', forkFrom],
    queryFn: () => apiFetch(`/api/strategies/${forkFrom}`),
    enabled: Boolean(forkFrom),
  })

  // Set form values from existing/fork strategy
  useEffect(() => {
    if (existingStrategy?.data) {
      setName(existingStrategy.data.name)
      setDescription(existingStrategy.data.description)
      setCode(existingStrategy.data.code)
      setStrategyType(existingStrategy.data.strategy_type)
      setIsPublic(existingStrategy.data.is_public)
    } else if (forkStrategy?.data) {
      setName(`${forkStrategy.data.name} (Fork)`)
      setDescription(`Forked from: ${forkStrategy.data.name}\n\n${forkStrategy.data.description}`)
      setCode(forkStrategy.data.code)
      setStrategyType(forkStrategy.data.strategy_type)
    }
  }, [existingStrategy, forkStrategy])

  // Create/Update mutation
  const mutation = useMutation({
    mutationFn: async (data: {
      name: string
      description: string
      code: string
      strategy_type: string
      is_public: boolean
    }) => {
      const endpoint = isEditing ? `/api/strategies/${id}` : '/api/strategies'
      const method = isEditing ? 'PATCH' : 'POST'
      
      return apiFetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...(apiKey && { 'Authorization': `Bearer ${apiKey}` }),
        },
        body: JSON.stringify(data),
      })
    },
    onSuccess: (data) => {
      setSuccess('Strategy saved successfully!')
      setTimeout(() => {
        navigate(`/strategies/${data.data?.id || id}`)
      }, 1500)
    },
    onError: (err: any) => {
      setError(err.message || 'Failed to save strategy')
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess('')

    if (!name.trim()) {
      setError('Strategy name is required')
      return
    }

    if (!code.trim()) {
      setError('Strategy code is required')
      return
    }

    if (!apiKey) {
      setShowApiKeyInput(true)
      setError('API key required to publish. Register your agent first.')
      return
    }

    mutation.mutate({
      name: name.trim(),
      description: description.trim(),
      code,
      strategy_type: strategyType,
      is_public: isPublic,
    })
  }

  const handleCompile = (compiledCode: string) => {
    console.log('Compiling strategy...')
    // In a real implementation, this would validate/compile the strategy
    setSuccess('Strategy compiled successfully! ✓')
    setTimeout(() => setSuccess(''), 3000)
  }

  const handleRun = (runCode: string) => {
    console.log('Running backtest...')
    // Navigate to backtest with the current code
    navigate(`/backtest?code=${encodeURIComponent(runCode)}`)
  }

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700 sticky top-0 z-50">
        <div className="max-w-full mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/strategies" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors">
              <span>←</span>
              <span>Back</span>
            </Link>
            <div className="h-6 w-px bg-gray-700"></div>
            <span className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
              {isEditing ? '✏️ Edit Strategy' : forkFrom ? '🔀 Fork Strategy' : '📝 New Strategy'}
            </span>
          </div>
          <div className="flex items-center gap-3">
            {error && (
              <span className="text-red-400 text-sm">{error}</span>
            )}
            {success && (
              <span className="text-green-400 text-sm">{success}</span>
            )}
            <button
              onClick={() => setShowApiKeyInput(!showApiKeyInput)}
              className="px-3 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 text-sm"
            >
              🔑 API Key
            </button>
            <button
              onClick={handleSubmit}
              disabled={mutation.isPending}
              className="px-4 py-2 bg-gradient-to-r from-purple-600 to-cyan-600 text-white rounded-lg hover:from-purple-500 hover:to-cyan-500 font-semibold disabled:opacity-50"
            >
              {mutation.isPending ? '⏳ Saving...' : isEditing ? '💾 Save Changes' : '🚀 Publish Strategy'}
            </button>
          </div>
        </div>

        {/* API Key Input */}
        {showApiKeyInput && (
          <div className="px-4 py-3 bg-gray-900/50 border-t border-gray-700">
            <div className="flex items-center gap-4 max-w-2xl">
              <label className="text-sm text-gray-400 whitespace-nowrap">Agent API Key:</label>
              <input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="kit_xxxxxxxx..."
                className="flex-1 bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white text-sm font-mono"
              />
              <Link to="/register" className="text-cyan-400 hover:text-cyan-300 text-sm whitespace-nowrap">
                Register Agent →
              </Link>
            </div>
          </div>
        )}
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar - Strategy Details */}
        <aside className="w-80 bg-gray-800/50 border-r border-gray-700 p-4 overflow-y-auto">
          <form onSubmit={handleSubmit}>
            {/* Name */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Strategy Name *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="My Awesome Strategy"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
              />
            </div>

            {/* Description */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe your strategy's logic, entry/exit conditions, risk management..."
                rows={4}
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent resize-none"
              />
            </div>

            {/* Strategy Type */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Strategy Type
              </label>
              <div className="grid grid-cols-2 gap-2">
                {strategyTypes.map((type) => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setStrategyType(type.value)}
                    className={`px-3 py-2 rounded-lg text-left text-sm transition-all ${
                      strategyType === type.value
                        ? 'bg-cyan-600/30 border border-cyan-500 text-cyan-400'
                        : 'bg-gray-900 border border-gray-700 text-gray-400 hover:bg-gray-800'
                    }`}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Visibility */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Visibility
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setIsPublic(true)}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm transition-all ${
                    isPublic
                      ? 'bg-green-600/30 border border-green-500 text-green-400'
                      : 'bg-gray-900 border border-gray-700 text-gray-400 hover:bg-gray-800'
                  }`}
                >
                  🌍 Public
                </button>
                <button
                  type="button"
                  onClick={() => setIsPublic(false)}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm transition-all ${
                    !isPublic
                      ? 'bg-yellow-600/30 border border-yellow-500 text-yellow-400'
                      : 'bg-gray-900 border border-gray-700 text-gray-400 hover:bg-gray-800'
                  }`}
                >
                  🔒 Private
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {isPublic
                  ? 'Anyone can view, star, and fork this strategy'
                  : 'Only you can see this strategy'}
              </p>
            </div>

            {/* Quick Actions */}
            <div className="border-t border-gray-700 pt-4">
              <h4 className="text-sm font-medium text-gray-300 mb-3">Quick Actions</h4>
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => handleCompile(code)}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-gray-300 hover:bg-gray-800 text-sm flex items-center gap-2"
                >
                  ✓ Validate Syntax
                </button>
                <button
                  type="button"
                  onClick={() => handleRun(code)}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-gray-300 hover:bg-gray-800 text-sm flex items-center gap-2"
                >
                  🧪 Quick Backtest
                </button>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(code)
                    setSuccess('Code copied!')
                    setTimeout(() => setSuccess(''), 2000)
                  }}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-gray-300 hover:bg-gray-800 text-sm flex items-center gap-2"
                >
                  📋 Copy Code
                </button>
              </div>
            </div>

            {/* Help */}
            <div className="border-t border-gray-700 pt-4 mt-4">
              <h4 className="text-sm font-medium text-gray-300 mb-2">Need Help?</h4>
              <ul className="text-xs text-gray-500 space-y-1">
                <li>• Use Pine Script v5 syntax</li>
                <li>• Include @version annotation</li>
                <li>• Define strategy() or indicator()</li>
                <li>• Ctrl+Enter to compile</li>
                <li>• Ctrl+Shift+Enter to run backtest</li>
              </ul>
              <Link
                to="/docs/kitscript"
                className="block mt-3 text-cyan-400 hover:text-cyan-300 text-sm"
              >
                📖 Read KitScript Documentation →
              </Link>
            </div>
          </form>
        </aside>

        {/* Main - Code Editor */}
        <main className="flex-1 flex flex-col overflow-hidden">
          <PineScriptEditor
            initialCode={code}
            onChange={setCode}
            onCompile={handleCompile}
            onRun={handleRun}
            showToolbar={true}
            showLineNumbers={true}
            height="100%"
          />
        </main>
      </div>
    </div>
  )
}
